/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Mar 29 2023
 **/

import { useTheme } from "@emotion/react";
import React, { useEffect, useState } from "react";

import { Properties } from "@/frameworks/libraries/properties";
import { AvatarStatus } from "../AvatarStatus";
import staticResource from "./staticResource";
import { AvatarBase } from ".";

type BaseAvatarProps = {
	kind:
		| "guest_24"
		| "people_24"
		| "people_28"
		| "people_32"
		| "space_24"
		| "space_32"
		| "space_40"
		| "space_64"
		| "space_80"
		| "group_40"
		| "people_40"
		| "people_48"
		| "people_64"
		| "people_120"
		| "group_60"
		| "group_64"
		| "group_80"
		| "people_80"
		| "people_24_new"
		| "people_28_new";
	src?: string;
	status?: "Green" | "Red" | "Gray" | "Yellow";
	text: string;
	textColor?:
		| "deco1_50_100"
		| "deco2_50_100"
		| "deco3_50_100"
		| "deco4_50_100"
		| "deco5_50_100"
		| "deco6_50_100"
		| "deco7_50_100"
		| "deco8_50_100"
		| "deco9_50_100"
		| "deco10_50_100"
		| "deco11_50_100"
		| "deco12_50_100"
		| "deco13_50_100"
		| "deco14_50_100"
		| "deco15_50_100"
		| "deco16_50_100"
		| "deco17_50_100"
		| "deco18_50_100"
		| "more";
	renderTopRight?: React.ReactNode;
	renderBottomRight?: React.ReactNode;
	renderBottomLeft?: React.ReactNode;
	renderTopLeft?: React.ReactNode;
};

/**
 * @description 3rd 이미지 리소스와 구분을 짓기 위한 상수
 */
const WEB_DOMAIN_INDICATOR = "/xrm";

export interface AvatarImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
	type: "image";
	onChangeType?: (type: "image" | "text") => void;
	onErrorImage?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

// 텍스트 아바타의 속성 정의
export interface AvatarTextProps extends React.HTMLAttributes<HTMLDivElement> {
	type: "text";
	onChangeType?: (type: "image" | "text") => void;
}

// AvatarProps 타입 정의
export type AvatarProps = BaseAvatarProps & (AvatarImageProps | AvatarTextProps);
/**
 * ! image모드의 리소스가 실패될 경우, text 모드로 auto change
 * @param [kind] relate in design system
 * @param [type] image or text
 * @param [src] type===image 일 경우의 이미지 리소스
 * @param [alt] type===image 일 경우의 image alt
 * @param [text] type===text 일 경우의 text
 * @param [textColor] type===text 일 경우의 text color
 * @returns
 */
export default function Avatar(props: AvatarProps) {
	const [type] = useState(props.type || "image");
	const { status } = sizeOfMap[props.kind];
	const metaProps = sizeOfMap[props.kind][type];
	const [src, setSrc] = useState(type === "image" && !props.src ? metaProps.defaultSrc : props.src || "");
	//@ts-ignore
	const textColorProps = useFontColorMap(props.textColor, props.kind.split("_")[0]);
	const size = metaProps.size;

	const isImageMode = (props: AvatarProps): props is BaseAvatarProps & AvatarImageProps => {
		return props.type === "image";
	};
	const handleError = (error: React.SyntheticEvent<HTMLImageElement, Event>) => {
		if (isImageMode(props)) {
			setSrc(sizeOfMap[props.kind].image.defaultSrc);
			props?.onErrorImage?.(error);
		}
	};

	/**
	 * 외부 요인에 의해 src가 바뀌었을 때, type이 image일 경우에만 src를 변경
	 */
	useEffect(() => {
		if (isImageMode(props) && props.src !== src) {
			setSrc(props.src || metaProps.defaultSrc);
		}
	}, [props.src]);

	if (
		!props.status &&
		!props.renderTopRight &&
		!props.renderBottomRight &&
		!props.renderBottomLeft &&
		!props.renderTopLeft
	) {
		return (
			<AvatarBase
				{...props}
				{...metaProps}
				{...textColorProps}
				type={type}
				onErrorImage={handleError}
				src={formatSrc(src || "")}
			/>
		);
	} else {
		return (
			<div
				css={{
					display: "inline-flex",
					position: "relative",
					width: size,
					height: size
				}}
			>
				<AvatarBase
					{...props}
					{...metaProps}
					{...textColorProps}
					type={type}
					onErrorImage={handleError}
					src={formatSrc(src || "")}
				/>
				{props.status && (
					<AvatarStatus
						status={props.status}
						size={status.size}
						css={{
							position: "absolute",
							bottom: 0,
							right: 0
						}}
					/>
				)}

				{props.renderTopRight && (
					<div
						css={{
							position: "absolute",
							top: 0,
							right: 0,
							transform: "translate(25%, -25%)"
						}}
					>
						{props.renderTopRight}
					</div>
				)}

				{props.renderBottomRight && (
					<div
						css={{
							position: "absolute",
							bottom: 0,
							right: 0,
							transform: "translate(25%, 25%)"
						}}
					>
						{props.renderBottomRight}
					</div>
				)}
				{props.renderBottomLeft && (
					<div
						css={{
							position: "absolute",
							bottom: 0,
							left: 0,
							transform: "translate(-25%, 25%)"
						}}
					>
						{props.renderBottomLeft}
					</div>
				)}
				{props.renderTopLeft && (
					<div
						css={{
							position: "absolute",
							top: 0,
							left: 0,
							transform: "translate(-25%, -25%)"
						}}
					>
						{props.renderTopLeft}
					</div>
				)}
			</div>
		);
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sizeOfMap: Record<BaseAvatarProps["kind"], any> = {
	guest_24: {
		image: {
			size: "24px",
			defaultSrc: staticResource.img_default_guest_24x24
		},
		text: {
			size: "24px",
			typoSize: "Body7"
		},
		status: {
			size: "Small"
		}
	},
	people_24_new: {
		image: {
			size: "24px",
			defaultSrc: staticResource.img_default_profile_new
		},
		text: {
			size: "24px",
			typoSize: "Body7"
		},
		status: {
			size: "Small"
		}
	},
	people_24: {
		image: {
			size: "24px",
			defaultSrc: staticResource.img_default_profile_24x24
		},
		text: {
			size: "24px",
			typoSize: "Body7"
		},
		status: {
			size: "Small"
		}
	},
	people_28: {
		image: {
			size: "28px",
			defaultSrc: staticResource.img_default_profile_32x32
		},
		text: {
			size: "28px",
			typoSize: "Body6"
		},
		status: {
			size: "Medium"
		}
	},
	people_28_new: {
		image: {
			size: "28px",
			defaultSrc: staticResource.img_default_profile_new
		},
		text: {
			size: "28px",
			typoSize: "Body6"
		},
		status: {
			size: "Medium"
		}
	},
	people_32: {
		image: {
			size: "32px",
			defaultSrc: staticResource.img_default_profile_32x32
		},
		text: {
			size: "32px",
			typoSize: "Body5_SB"
		},
		status: {
			size: "Medium"
		}
	},
	space_24: {
		image: {
			size: "24px",
			borderRadius: "12px",
			defaultSrc: staticResource.img_default_24x24
		},
		text: {
			size: "24px",
			typoSize: "Body7"
		},
		status: {
			size: "Small"
		}
	},
	space_32: {
		image: {
			size: "32px",
			borderRadius: "12px",
			defaultSrc: staticResource.img_default_32x32
		},
		text: {
			size: "32px",
			borderRadius: "12px"
		},
		status: {
			size: "Medium"
		}
	},
	space_40: {
		image: {
			size: "40px",
			borderRadius: "8px",
			defaultSrc: staticResource.img_default_40x40
		},
		text: {
			size: "40px",
			borderRadius: "8px"
		},
		status: {
			size: "Medium"
		}
	},
	space_64: {
		image: {
			size: "64px",
			borderRadius: "21px",
			defaultSrc: staticResource.img_default_64x64
		},
		text: {
			size: "64px",
			borderRadius: "21px"
		},
		status: {
			size: "Large"
		}
	},
	space_80: {
		image: {
			size: "80px",
			borderRadius: "12px",
			defaultSrc: staticResource.img_default_64x64
		},
		text: {
			size: "80px",
			borderRadius: "12px"
		},
		status: {
			size: "Large"
		}
	},
	people_40: {
		image: {
			size: "40px",
			defaultSrc: staticResource.img_default_profile_40x40
		},
		text: {
			size: "40px",
			typoSize: "Title2"
		},
		status: {
			size: "Medium"
		}
	},
	group_40: {
		image: {
			size: "40px",
			borderRadius: "6px",
			defaultSrc: staticResource.img_default_64x64
		},
		text: {
			size: "40px",
			borderRadius: "6px",
			typoSize: "Body5_SB"
		},
		status: {
			size: "Medium"
		}
	},
	people_48: {
		image: {
			size: "48px",
			defaultSrc: staticResource.img_default_profile_60x60
		},
		text: {
			size: "48px",
			typoSize: "Title2"
		},
		status: {
			size: "Medium"
		}
	},
	people_64: {
		image: {
			size: "64px",
			defaultSrc: staticResource.img_default_profile_60x60
		},
		text: {
			size: "64px",
			typoSize: "Head5"
		},
		status: {
			size: "Large"
		}
	},
	people_120: {
		image: {
			size: "120px",
			defaultSrc: staticResource.img_default_profile_80x80
		},
		text: {
			size: "120px",
			typoSize: "Head5"
		},
		status: {
			size: "Large"
		}
	},
	group_60: {
		image: {
			size: "60px",
			defaultSrc: staticResource.img_default_64x64
		},
		text: {
			borderRadius: "12px",
			size: "60px",
			typoSize: "Title2"
		},
		status: {
			size: "Large"
		}
	},
	group_64: {
		image: {
			size: "60px",
			defaultSrc: staticResource.img_default_64x64
		},
		text: {
			borderRadius: "21px",
			size: "64px",
			typoSize: "Title2"
		},
		status: {
			size: "Large"
		}
	},
	group_80: {
		image: {
			size: "80px",
			defaultSrc: staticResource.img_default_64x64
		},
		text: {
			borderRadius: "12px",
			size: "80px",
			typoSize: "Head6"
		},
		status: {
			size: "Large"
		}
	},
	people_80: {
		image: {
			size: "80px",
			defaultSrc: staticResource.img_default_profile_60x60
		},
		text: {
			size: "80px",
			typoSize: "Head5"
		},
		status: {
			size: "Large"
		}
	}
};

const formatSrc = (src: string) => {
	return isValidURL(src) ? src : src.includes(WEB_DOMAIN_INDICATOR) ? `${Properties.property.webDomain}${src}` : src;
};

const useFontColorMap = (textThemeColor: BaseAvatarProps["textColor"], type: "people" | "group" | "space") => {
	const theme = useTheme();
	const colorType = type === "people" ? "people" : "group";
	const fontColorMap = {
		people: {
			deco1_50_100: {
				backgroundColor: theme.colors.deco1_10_100,
				textColor: theme.colors.deco1_50_100
			},
			deco2_50_100: {
				backgroundColor: theme.colors.deco2_10_100,
				textColor: theme.colors.deco2_50_100
			},
			deco3_50_100: {
				backgroundColor: theme.colors.deco3_10_100,
				textColor: theme.colors.deco3_50_100
			},
			deco4_50_100: {
				backgroundColor: theme.colors.deco4_10_100,
				textColor: theme.colors.deco4_50_100
			},
			deco5_50_100: {
				backgroundColor: theme.colors.deco5_10_100,
				textColor: theme.colors.deco5_50_100
			},
			deco6_50_100: {
				backgroundColor: theme.colors.deco6_10_100,
				textColor: theme.colors.deco6_50_100
			},
			deco7_50_100: {
				backgroundColor: theme.colors.deco7_10_100,
				textColor: theme.colors.deco7_50_100
			},
			deco8_50_100: {
				backgroundColor: theme.colors.deco8_10_100,
				textColor: theme.colors.deco8_50_100
			},
			deco9_50_100: {
				backgroundColor: theme.colors.deco9_10_100,
				textColor: theme.colors.deco9_50_100
			},
			deco10_50_100: {
				backgroundColor: theme.colors.deco10_10_100,
				textColor: theme.colors.deco10_50_100
			},
			deco11_50_100: {
				backgroundColor: theme.colors.deco11_10_100,
				textColor: theme.colors.deco11_50_100
			},
			deco12_50_100: {
				backgroundColor: theme.colors.deco12_10_100,
				textColor: theme.colors.deco12_50_100
			},
			deco13_50_100: {
				backgroundColor: theme.colors.deco13_10_100,
				textColor: theme.colors.deco13_50_100
			},
			deco14_50_100: {
				backgroundColor: theme.colors.deco14_10_100,
				textColor: theme.colors.deco14_50_100
			},
			deco15_50_100: {
				backgroundColor: theme.colors.deco15_10_100,
				textColor: theme.colors.deco15_50_100
			},
			deco16_50_100: {
				backgroundColor: theme.colors.deco16_10_100,
				textColor: theme.colors.deco16_50_100
			},
			deco17_50_100: {
				backgroundColor: theme.colors.deco17_10_100,
				textColor: theme.colors.deco17_50_100
			},
			deco18_50_100: {
				backgroundColor: theme.colors.deco18_10_100,
				textColor: theme.colors.deco18_50_100
			},
			more: {
				backgroundColor: theme.colors.neutral_70_100,
				textColor: theme.colors.secondary_80_12
			}
		},
		group: {
			deco1_50_100: {
				textColor: theme.colors.deco1_10_100,
				backgroundColor: theme.colors.deco1_50_100
			},
			deco2_50_100: {
				textColor: theme.colors.deco2_10_100,
				backgroundColor: theme.colors.deco2_50_100
			},
			deco3_50_100: {
				textColor: theme.colors.deco3_10_100,
				backgroundColor: theme.colors.deco3_50_100
			},
			deco4_50_100: {
				textColor: theme.colors.deco4_10_100,
				backgroundColor: theme.colors.deco4_50_100
			},
			deco5_50_100: {
				textColor: theme.colors.deco5_10_100,
				backgroundColor: theme.colors.deco5_50_100
			},
			deco6_50_100: {
				textColor: theme.colors.deco6_10_100,
				backgroundColor: theme.colors.deco6_50_100
			},
			deco7_50_100: {
				textColor: theme.colors.deco7_10_100,
				backgroundColor: theme.colors.deco7_50_100
			},
			deco8_50_100: {
				textColor: theme.colors.deco8_10_100,
				backgroundColor: theme.colors.deco8_50_100
			},
			deco9_50_100: {
				textColor: theme.colors.deco9_10_100,
				backgroundColor: theme.colors.deco9_50_100
			},
			deco10_50_100: {
				textColor: theme.colors.deco10_10_100,
				backgroundColor: theme.colors.deco10_50_100
			},
			deco11_50_100: {
				textColor: theme.colors.deco11_10_100,
				backgroundColor: theme.colors.deco11_50_100
			},
			deco12_50_100: {
				textColor: theme.colors.deco12_10_100,
				backgroundColor: theme.colors.deco12_50_100
			},
			deco13_50_100: {
				textColor: theme.colors.deco13_10_100,
				backgroundColor: theme.colors.deco13_50_100
			},
			deco14_50_100: {
				textColor: theme.colors.deco14_10_100,
				backgroundColor: theme.colors.deco14_50_100
			},
			deco15_50_100: {
				textColor: theme.colors.deco15_10_100,
				backgroundColor: theme.colors.deco15_50_100
			},
			deco16_50_100: {
				textColor: theme.colors.deco16_10_100,
				backgroundColor: theme.colors.deco16_50_100
			},
			deco17_50_100: {
				textColor: theme.colors.deco17_10_100,
				backgroundColor: theme.colors.deco17_50_100
			},
			deco18_50_100: {
				textColor: theme.colors.deco18_10_100,
				backgroundColor: theme.colors.deco18_50_100
			},
			more: {
				textColor: theme.colors.neutral_70_100,
				backgroundColor: theme.colors.secondary_80_12
			}
		}
	};
	return fontColorMap[colorType][textThemeColor!];
};

function isValidURL(url: string) {
	if (url.startsWith("data:")) {
		const regex = /^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+)?;base64,/;
		return regex.test(url);
	}

	try {
		const parsedUrl = new URL(url);
		return parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:";
	} catch (e) {
		return false;
	}
}
